// Single source for the tracker bootstraps: _document.tsx inlines them
// (guarded) and ConsentScripts.tsx injects them on a mid-session grant.

import { CONSENT_COOKIE_NAME, CONSENT_VERSION } from "@/consent/consent";
import {
  GTM_CONTAINER_ID,
  KLAVIYO_PUBLIC_KEY,
  META_PIXEL_ID,
  OPENAI_PIXEL_ID,
  PINTEREST_TAG_ID,
  PODSCRIBE_USER_ID,
  TATARI_TAG_ID,
  X_PIXEL_ID,
} from "@/utils/constants";

export const META_PIXEL_SNIPPET = `!function(f,b,e,v,n,t,s)
{if(f.fbq)return;n=f.fbq=function(){n.callMethod?
n.callMethod.apply(n,arguments):n.queue.push(arguments)};
if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
n.queue=[];t=b.createElement(e);t.async=!0;
t.src=v;s=b.getElementsByTagName(e)[0];
s.parentNode.insertBefore(t,s)}(window, document,'script',
'https://connect.facebook.net/en_US/fbevents.js');
fbq('init', '${META_PIXEL_ID}');
fbq('track', 'PageView');`;

export const X_PIXEL_SNIPPET = `!function(e,t,n,s,u,a){e.twq||(s=e.twq=function(){s.exe?s.exe.apply(s,arguments):s.queue.push(arguments);
},s.version='1.1',s.queue=[],u=t.createElement(n),u.async=!0,u.src='https://static.ads-twitter.com/uwt.js',
a=t.getElementsByTagName(n)[0],a.parentNode.insertBefore(u,a))}(window,document,'script');
twq('config','${X_PIXEL_ID}');`;

// Pinterest base tag, without its enhanced-match email: we have no email at
// page load, and the noscript image can't honour consent.
export const PINTEREST_TAG_SNIPPET = `!function(e){if(!window.pintrk){window.pintrk=function(){
window.pintrk.queue.push(Array.prototype.slice.call(arguments))};var
n=window.pintrk;n.queue=[],n.version="3.0";var
t=document.createElement("script");t.async=!0,t.src=e;var
r=document.getElementsByTagName("script")[0];
r.parentNode.insertBefore(t,r)}}("https://s.pinimg.com/ct/core.js");
pintrk('load','${PINTEREST_TAG_ID}');
pintrk('page');`;

// OpenAI Ads pixel, from Ads Manager. It only ever runs once marketing is
// granted, so it grants consent itself: that lifts a denial the pixel stored on
// an earlier revoke. Page views come from OpenAIPageView, not the snippet, so a
// client-side navigation counts too.
export const OPENAI_PIXEL_SNIPPET = `!function(w,d,s,u){if(w.oaiq)return;var q=function(){q.q.push(arguments)};q.q=[];w.oaiq=q;var j=d.createElement(s);j.async=1;j.src=u;var f=d.getElementsByTagName(s)[0];f.parentNode.insertBefore(j,f)}(window,document,"script","https://bzrcdn.openai.com/sdk/oaiq.min.js");
oaiq("consent",true);
oaiq("init",{pixelId:"${OPENAI_PIXEL_ID}",debug:${process.env.NODE_ENV !== "production"}});`;

// Klaviyo onsite: the loader plus Klaviyo's own `klaviyo` object stub, which
// queues track/identify calls made before klaviyo.js arrives.
export const KLAVIYO_ONSITE_SNIPPET = `(function(d){var s=d.createElement('script');s.async=true;s.src='https://static.klaviyo.com/onsite/js/${KLAVIYO_PUBLIC_KEY}/klaviyo.js?company_id=${KLAVIYO_PUBLIC_KEY}';s.setAttribute('data-klaviyo-onsite','');var f=d.getElementsByTagName('script')[0];f.parentNode.insertBefore(s,f);})(document);
!function(){if(!window.klaviyo){window._klOnsite=window._klOnsite||[];try{window.klaviyo=new Proxy({},{get:function(n,i){return"push"===i?function(){var n;(n=window._klOnsite).push.apply(n,arguments)}:function(){for(var n=arguments.length,o=new Array(n),w=0;w<n;w++)o[w]=arguments[w];var t="function"==typeof o[o.length-1]?o.pop():void 0,e=new Promise((function(n){window._klOnsite.push([i].concat(o,[function(i){t&&t(i),n(i)}]))}));return e}}})}catch(n){window.klaviyo=window.klaviyo||[],window.klaviyo.push=function(){var n;(n=window._klOnsite).push.apply(n,arguments)}}}}();`;

export const TATARI_PIXEL_SNIPPET = `!function(){try{!function(t,e){if(!e.version){window.tatari=e,e.init=function(t,i){var a=function(t,i){e[i]=function(){t.push([i].concat(Array.prototype.slice.call(arguments,0)))}};"track pageview identify addToCart purchase checkoutStarted searchSubmitted signUp leadSubmitted productViewed".split(" ").forEach(function(t){a(e,t)}),e._i=t,e.config=i,e.pageview()},e.version="1.2.18";var i=t.createElement("script");i.type="text/javascript",i.async=!0,i.src="https://d2hrivdxn8ekm8.cloudfront.net/tag-manager/${TATARI_TAG_ID}-latest.js";var a=t.getElementsByTagName("script")[0];a.parentNode.insertBefore(i,a)}}(document,window.tatari||[])}catch(t){console.log(t)}}();tatari.init('${TATARI_TAG_ID}');`;

// impact.com Universal Tracking Tag — copied verbatim from the install docs.
export const IMPACT_UTT_SNIPPET = `(function(a,b,c,d,e,f,g){e['ire_o']=c;e[c]=e[c]||function(){(e[c].a=e[c].a||[]).push(arguments)};f=d.createElement(b);g=d.getElementsByTagName(b)[0];f.async=1;f.src=a;g.parentNode.insertBefore(f,g);})('https://utt.impactcdn.com/A7391114-8048-4ecf-bfcd-22f31f3f3de11.js','script','ire',document,window);`;

// Podscribe view pixel — SDK loader dispatches a 'podscribe:ready' event once
// window.podscribe is initialized, which PodscribeView subscribes to before
// firing its one-shot view event.
export const PODSCRIBE_SNIPPET = `(function(w,d){var e=d.createElement('script');e.id='podscribe-capture';e.async=true;e.src='https://d34r8q7sht0t9k.cloudfront.net/tag.js';var s=d.getElementsByTagName('script')[0];s.parentNode.insertBefore(e,s);e.addEventListener('load',function(){w.podscribe('init',{user_id:'${PODSCRIBE_USER_ID}',advertiser:'maticrobots'});w.dispatchEvent(new Event('podscribe:ready'));});})(window,document);`;

export const GTM_SNIPPET = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${GTM_CONTAINER_ID}');`;

// Runs as an inline <script> before any bundle exists, so it must stay
// dependency-free; a parse failure falls through to loading (default-allow).
export function withMarketingGuard(snippet: string): string {
  return (
    `(function(){try{` +
    `var m=document.cookie.match(/(?:^|; ?)${CONSENT_COOKIE_NAME}=v${CONSENT_VERSION}\\.a\\d\\.m(\\d)/);` +
    `if(m?m[1]==="0":navigator.globalPrivacyControl===true)return;` +
    `}catch(e){console.error("Failed to read consent for tracking guard:",e);}` +
    `${snippet}})();`
  );
}

export type MarketingTracker = {
  id: string;
  snippet: string;
  isLoaded: () => boolean;
};

export const MARKETING_TRACKERS: MarketingTracker[] = [
  {
    id: "meta-pixel",
    snippet: META_PIXEL_SNIPPET,
    isLoaded: () => typeof window.fbq === "function",
  },
  {
    id: "x-pixel",
    snippet: X_PIXEL_SNIPPET,
    isLoaded: () => typeof window.twq === "function",
  },
  {
    id: "pinterest-tag",
    snippet: PINTEREST_TAG_SNIPPET,
    isLoaded: () => typeof window.pintrk === "function",
  },
  {
    id: "openai-pixel",
    snippet: OPENAI_PIXEL_SNIPPET,
    isLoaded: () => typeof window.oaiq === "function",
  },
  {
    id: "klaviyo-onsite",
    snippet: KLAVIYO_ONSITE_SNIPPET,
    isLoaded: () => !!document.querySelector("script[data-klaviyo-onsite]"),
  },
  {
    id: "tatari",
    snippet: TATARI_PIXEL_SNIPPET,
    isLoaded: () => typeof window.tatari?.pageview === "function",
  },
  {
    id: "impact-utt",
    snippet: IMPACT_UTT_SNIPPET,
    isLoaded: () => typeof window.ire === "function",
  },
  {
    id: "podscribe",
    snippet: PODSCRIBE_SNIPPET,
    isLoaded: () => typeof window.podscribe === "function",
  },
  {
    id: "gtm",
    snippet: GTM_SNIPPET,
    isLoaded: () => !!window.google_tag_manager?.[GTM_CONTAINER_ID],
  },
];
